import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";

export default function TestCardsModal({
    open,
    onClose,
    deckId,
    deckName,
    deckCardsMeta = [],
}) {
    // Get the token from localStorage
    const token = JSON.parse(localStorage?.getItem("zubrus_user_data"))?.data?.tokens?.accessToken;

    // Build the Flutter web view URL with token and deckId
    const flutterWebViewUrl = `https://app-preview.zubrus.lt/iframe-entry?token=${encodeURIComponent(token || '')}&deckId=${encodeURIComponent(deckId || '')}&duration=5`;

    return (
        <Dialog open={open} as="div" className="relative z-50" onClose={onClose}>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                <DialogPanel className="w-full max-w-md bg-white rounded-3xl relative max-h-[95vh]">
                    <IoMdClose
                        className="absolute top-4 right-4 text-gray-600 cursor-pointer z-10"
                        onClick={onClose}
                        size={22}
                    />

                    <div className="p-6 flex flex-col gap-4 max-h-[90vh]">
                        <div>
                            <h3 className="text-2xl font-semibold">{deckName || "Deck"}</h3>
                        </div>

                        {/* Flutter Web View iframe */}
                        <div className="w-full h-[600px] rounded-lg overflow-hidden">
                            <iframe
                                src={flutterWebViewUrl}
                                title="Flutter Card Test"
                                className="w-full h-full border-0"
                                allow="microphone; camera"
                            />
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}


/* ============================================
   COMMENTED OUT ORIGINAL CODE - START
   ============================================

import { useEffect, useMemo, useState, useRef } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { FaQuestionCircle, FaMicrophone } from "react-icons/fa";
import { IoArrowForward } from "react-icons/io5";
import { FaStar } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { ApiGet } from "../../../helper/axiosFunctions";
import { commonToast } from "../../../helper/Toast";
import "./TestCardsModal.css";

const SwipeCard = ({ children, onSwipe, canSwipe = true }) => {
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const start = useRef({ x: 0, y: 0 });

    const onDown = (e) => {
        setDragging(true);
        start.current = { x: e.clientX, y: e.clientY };
    };

    const onMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;
        setPos({ x: dx, y: dy });
    };

    const onUp = () => {
        if (!dragging) return;
        setDragging(false);

        if (Math.abs(pos.x) > 140) {
            if (!canSwipe) {
                setPos({ x: 0, y: 0 });
                return;
            }

            const dir = pos.x > 0 ? 1 : -1;
            setPos({ x: dir * 500, y: pos.y });
            setTimeout(() => onSwipe?.(), 250);
            return;
        }

        setPos({ x: 0, y: 0 });
    };

    return (
        <div
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerLeave={onUp}
            className="w-full cursor-pointer select-none transition-transform"
            style={{
                transform: `
                    translate(${pos.x}px, ${pos.y}px)
                    rotate(${pos.x * 0.05}deg)
                `,
                transition: dragging ? "none" : "transform 0.25s ease-out",
                transformStyle: "preserve-3d",
                willChange: "transform",
            }}
        >
            {children}
        </div>
    );
};

export default function TestCardsModal({
    open,
    onClose,
    deckId,
    deckName,
    deckCardsMeta = [],
}) {
    const { t } = useTranslation();
    const [cards, setCards] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showHints, setShowHints] = useState({});
    const [inputValues, setInputValues] = useState({});
    const [showAnswerModal, setShowAnswerModal] = useState(false);
    const [selectedCardAnswer, setSelectedCardAnswer] = useState(null);
    const [flippedCards, setFlippedCards] = useState({});
    const [revealedLetters, setRevealedLetters] = useState({});
    const [submittedAnswers, setSubmittedAnswers] = useState({});

    const complexityMap = useMemo(() => {
        if (!deckCardsMeta?.length) return {};
        return deckCardsMeta.reduce((acc, item) => {
            if (item?.cardId) acc[item.cardId] = item?.complexity;
            return acc;
        }, {});
    }, [deckCardsMeta]);

    useEffect(() => {
        if (!open || !deckId) return;

        let isMounted = true;

        const fetchCards = async () => {
            try {
                setIsLoading(true);
                const response = await ApiGet(`/contents/decks/${deckId}/cards`);
                if (isMounted) {
                    const fetchedCards = response?.data || response || [];
                    setCards(fetchedCards);
                    setCurrentIndex(0);
                    setFlippedCards({});
                    setRevealedLetters({});
                    setSubmittedAnswers({});
                }
            } catch (error) {
                commonToast(
                    error?.data?.errorDescription ||
                    error?.data?.error ||
                    error?.message ||
                    t("somethingWentWrong", { defaultValue: "Something went wrong" }),
                    "error"
                );
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchCards();
        return () => (isMounted = false);
    }, [open, deckId, t]);

    useEffect(() => {
        setCurrentIndex(0);
        setShowHints({});
        setInputValues({});
        setShowAnswerModal(false);
        setSelectedCardAnswer(null);
        setFlippedCards({});
        setRevealedLetters({});
        setSubmittedAnswers({});
    }, [open]);

    const handleOpenAnswerModal = (card) => {
        const answerParts = card?.answers?.[0]?.parts || [];
        const answer = answerParts?.find((part) =>
            part.contentType?.includes("text")
        )?.contentBody;

        // Get hints if available
        const hints = card?.hints || [];

        const hasImageContent =
            hints?.some((hint) =>
                hint?.parts?.some((part) => part?.contentType?.startsWith("image"))
            ) ||
            answerParts?.some((part) => part?.contentType?.startsWith("image"));

        // Combine answer and hints
        const answerData = {
            answer: answer || null,
            answerParts,
            hints: hints,
            card: card,
            hasImageContent,
        };

        setSelectedCardAnswer(answerData);
        setShowAnswerModal(true);
    };

    const renderContentPart = (part, key, align = "left") => {
        if (part.contentType?.startsWith("text")) {
            return (
                <div
                    key={key}
                    className={`text-base text-gray-800 leading-relaxed ${align === "center" ? "text-center" : "text-left"}`}
                    dangerouslySetInnerHTML={{ __html: part.contentBody }}
                />
            );
        }
        if (part.contentType?.startsWith("image")) {
            return (
                <img
                    key={key}
                    src={part.contentBody}
                    alt="Content"
                    className="max-w-full h-auto rounded-lg border mx-auto my-4"
                    onError={(e) => {
                        e.target.style.display = 'none';
                    }}
                />
            );
        }
        return null;
    };

    const toggleFlip = (cardKey, e) => {
        if (e) {
            e.stopPropagation();
        }
        setFlippedCards((prev) => ({
            ...prev,
            [cardKey]: !prev[cardKey],
        }));
    };

    const handleCardClick = (card, cardIndex, e) => {
        const cardKey = card?.cardId || cardIndex;
        const answerType = card?.answerType?.toLowerCase() || "";
        const isMemory = answerType === "memory";
        
        if (isMemory) {
            toggleFlip(cardKey, e);
        }
        // For input type, we don't auto-flip on card click, only on submit
    };

    const renderCard = (card, cardIndex = null) => {
        if (!card) return null;

        const answerType = card?.answerType?.toLowerCase() || "";
        const isMemory = answerType === "memory";
        const isInput = answerType === "input";

        const questionImage = card?.question?.parts?.find(
            (part) =>
                part.contentType?.includes("image") ||
                part.contentType?.includes("unknown")
        )?.contentBody;

        const question = card?.question?.parts?.find((part) =>
            part.contentType?.includes("text")
        )?.contentBody;

        const answer = card?.answers?.[0]?.parts?.find((part) =>
            part.contentType?.includes("text")
        )?.contentBody;

        // Get full answer text (cleaned of HTML)
        const fullAnswerText = answer ? answer.replace(/<[^>]*>/g, '').trim() : '';
        const firstLetter = fullAnswerText ? fullAnswerText.charAt(0) : '';

        const cardKey = card?.cardId || cardIndex;
        const showHint = showHints[cardKey] || false;
        const inputValue = inputValues[cardKey] || '';
        const revealedCount = revealedLetters[cardKey] || 0;
        const isFlipped = flippedCards[cardKey] || false;
        const isSubmitted = submittedAnswers[cardKey] || false;
        const cardContainerStyle = {
            perspective: "1000px",
            transformStyle: "preserve-3d"
        };
        const cardFlipStyle = {
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
            transition: "transform 0.6s ease-in-out",
            transformStyle: "preserve-3d",
            WebkitTransformStyle: "preserve-3d"
        };
        const cardFaceStyle = {
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden"
        };

        const handleShowHint = (e) => {
            e.stopPropagation();
            setShowHints(prev => ({ ...prev, [cardKey]: !prev[cardKey] }));
            if (!showHints[cardKey]) {
                // Reset revealed letters when showing hint for first time
                setRevealedLetters(prev => ({ ...prev, [cardKey]: 1 }));
            } else {
                // Reset when hiding hint
                setRevealedLetters(prev => ({ ...prev, [cardKey]: 0 }));
            }
        };

        const handleShowNextLetter = (e) => {
            e.stopPropagation();
            if (fullAnswerText && revealedCount < fullAnswerText.length) {
                const newRevealedCount = revealedCount + 1;
                setRevealedLetters(prev => ({ ...prev, [cardKey]: newRevealedCount }));
            }
        };

        const handleInputChange = (e) => {
            e.stopPropagation();
            setInputValues(prev => ({ ...prev, [cardKey]: e.target.value }));
        };

        const handleSubmit = (e) => {
            e.stopPropagation();
            // Mark answer as submitted
            setSubmittedAnswers((prev) => ({
                ...prev,
                [cardKey]: true,
            }));
            // Flip the card to show answer
            setFlippedCards((prev) => ({
                ...prev,
                [cardKey]: true,
            }));
        };

        const handleNextQuestion = (e) => {
            e.stopPropagation();
            // Flip back to front (same card)
            setFlippedCards((prev) => ({
                ...prev,
                [cardKey]: false,
            }));
        };

        const answerParts = card?.answers?.[0]?.parts || [];

        const renderHintContent = (prefix = "hint", align = "left") => {
            if (card?.hints?.length) {
                return card.hints.flatMap((hint, hintIdx) =>
                    hint.parts?.map((part, partIdx) =>
                        renderContentPart(part, `${prefix}-${hintIdx}-${partIdx}`, align)
                    )
                );
            }

            if (firstLetter) {
                return (
                    <span className={`text-2xl font-semibold text-gray-800 block ${align === "center" ? "text-center" : "text-left"}`}>
                        {firstLetter}
                    </span>
                );
            }

            return (
                <span className={`text-sm text-gray-600 ${align === "center" ? "text-center" : "text-left"}`}>
                    {t("No hints available") || "No hints available"}
                </span>
            );
        };

        const renderAnswerContent = (align = "left") => {
            if (answerParts?.length) {
                return answerParts.map((part, idx) =>
                    renderContentPart(part, `answer-${idx}`, align)
                );
            }

            if (answer) {
                return (
                    <div
                        className={`text-base text-gray-800 leading-relaxed ${align === "center" ? "text-center" : "text-left"}`}
                        dangerouslySetInnerHTML={{ __html: answer }}
                    />
                );
            }

            return (
                <span className={`text-sm text-gray-600 ${align === "center" ? "text-center" : "text-left"}`}>
                    {t("No answer available") || "No answer available"}
                </span>
            );
        };

        // Memory type: Use flip card with click event
        if (isMemory) {
            return (
                <div 
                    className="flip-card-container"
                    style={cardContainerStyle}
                    onClick={(e) => handleCardClick(card, cardIndex, e)}
                >
                    <div
                        className={`flip-card ${isFlipped ? "flipped" : ""}`}
                        style={cardFlipStyle}
                    >
                        {/- Front side - Question -/}
                        <div className="flip-card-front p-4" style={cardFaceStyle}>
                            <div className="flip-card-body flex items-center justify-center">
                                <div className="w-full text-center">
                                    <div
                                        className="text-gray-800 text-center text-base leading-relaxed font-medium"
                                        dangerouslySetInnerHTML={{ __html: question || "N/A" }}
                                    />

                                    {questionImage && (
                                        <div className="w-full rounded-2xl overflow-hidden mt-6">
                                            <img
                                                src={questionImage}
                                                alt="Question"
                                                className="w-full h-full object-contain max-h-56 mx-auto"
                                                draggable={false}
                                                onDragStart={(e) => e.preventDefault()}
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flip-card-footer">
                                <div className="flex items-center gap-3 p-4">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenAnswerModal(card);
                                        }}
                                        className="flex items-center justify-center w-10 h-10 bg-orange-500 hover:bg-orange-600 rounded-full transition-colors shadow-md"
                                    >
                                        <FaQuestionCircle className="text-white text-2xl" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/- Back side - Answer -/}
                        <div className="flip-card-back p-4" style={cardFaceStyle}>
                            <div className="flip-card-body space-y-4 flex items-center justify-center">
                                <div className="space-y-3 text-center w-full">
                                    {card?.hints?.length
                                        ? renderHintContent("memory-hint", "center")
                                        : renderAnswerContent("center")}
                                </div>
                            </div>

                            <div className="flip-card-footer">
                                <div className="flex items-start gap-3 mb-3">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenAnswerModal(card);
                                        }}
                                        className="flex items-center justify-center w-10 h-10 bg-orange-500 rounded-full transition-colors shadow-md flex-shrink-0"
                                    >
                                        <FaQuestionCircle className="text-white text-2xl" />
                                    </button>
                                </div>
                                <hr className="my-2 border-gray-200 -mx-4 w-[calc(100%+2rem)]" />
                                <div className="flex items-center gap-0.5 w-full px-6 pt-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Handle incorrect/dead response
                                        }}
                                        className="flex-1 py-1 rounded-s-full bg-red-500 hover:bg-red-600 active:bg-red-700 transition-colors flex items-center justify-center shadow-sm min-h-[36px]"
                                    >
                                        <span className="text-xl leading-none">😵</span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Handle neutral response
                                        }}
                                        className="flex-1 py-1  bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 transition-colors flex items-center justify-center shadow-sm min-h-[36px]"
                                    >
                                        <span className="text-xl leading-none">🙂</span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Handle correct response
                                        }}
                                        className="flex-1 py-1 rounded-e-full bg-green-500 hover:bg-green-600 active:bg-green-700 transition-colors flex items-center justify-center shadow-sm min-h-[36px]"
                                    >
                                        <span className="text-xl leading-none">😄</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Input type: Use flip card structure with manual flip control
        return (
            <div className="flip-card-container" style={cardContainerStyle}>
                <div
                    className={`flip-card ${isFlipped ? "flipped" : ""}`}
                    style={cardFlipStyle}
                >
                    {/- Front side - Question with input -/}
                    <div className="flip-card-front" style={cardFaceStyle}>
                        <div className="flip-card-body flex flex-col gap-3 p-4">
                            <div className="flex-1 flex items-center justify-center">
                                <div
                                    className="text-gray-700 text-center text-lg leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: question || "N/A" }}
                                />
                            </div>

                            {questionImage && (
                                <div className="w-full rounded-lg overflow-hidden">
                                    <img
                                        src={questionImage}
                                        alt="Question"
                                        className="w-full h-full object-contain max-h-56"
                                        draggable={false}
                                        onDragStart={(e) => e.preventDefault()}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}

                            <div className="flex items-center gap-2 mt-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAnswerModal(card);
                                    }}
                                    className="flex items-center justify-center w-10 h-10 bg-orange-500 hover:bg-orange-600 rounded-full transition-colors shadow-md"
                                >
                                    <FaQuestionCircle className="text-white text-2xl" />
                                </button>
                                {!isSubmitted && (
                                    <>
                                        {showHint && revealedCount > 0 && revealedCount < fullAnswerText.length ? (
                                            <button
                                                onClick={handleShowNextLetter}
                                                className="px-4 py-2 rounded-full border border-gray-300 text-sm"
                                            >
                                                Show next letter
                                            </button>
                                        ) : showHint && revealedCount >= fullAnswerText.length ? null : (
                                            <button
                                                onClick={handleShowHint}
                                                className="px-4 py-2 rounded-full border border-gray-300 text-sm"
                                            >
                                                Show First Letter
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>

                            {!isSubmitted && (
                                <>
                                    <hr className="border-gray-200 -mx-4 w-[calc(100%+2rem)]" /> 

                                    <div className="flex items-center gap-2 mt-auto pt-2 pb-2">
                                        <FaMicrophone className="text-gray-500 text-xl cursor-pointer hover:text-gray-700 flex-shrink-0" />
                                        <input
                                            type="text"
                                            value={inputValue}
                                            onChange={handleInputChange}
                                            placeholder={showHint && revealedCount > 0 ? fullAnswerText.substring(0, revealedCount) : (t("Enter your answer") || "Enter your answer")}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <button
                                            onClick={handleSubmit}
                                            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 flex-shrink-0 transition-colors"
                                        >
                                            <IoArrowForward className="text-white" size={20} />
                                        </button>
                                    </div>
                                </>
                            )}

                            {/- Answer not submitted status on front side -/}
                            {isSubmitted && (
                                <div className="mt-auto w-full">
                                    <hr className=" -mx-4 w-[calc(100%+2rem)]" />
                                    <div className="-mx-4 w-[calc(100%+2rem)] bg-red-50 border-t  border-gray-100 px-4 py-3 flex items-center  gap-2 ">
                                        <span className="text-red-600 text-sm">Answer not submitted</span>
                                        <button 
                                            className="text-red-600 text-2xl leading-none pb-1"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/- Back side - Answer -/}
                    <div className="flip-card-back p-4" style={cardFaceStyle}>
                        <div className="flip-card-body flex items-center justify-center">
                            <div className="w-full text-center">
                                <div className="text-gray-800 text-center text-lg leading-relaxed font-medium">
                                    {renderAnswerContent("center")}
                                </div>
                            </div>
                        </div>

                        <div className="flip-card-footer">
                            {/- Answer not submitted status -/}
                            <hr className="-mx-4 w-[calc(100%+2rem)]" />
                            <div className="-mx-4 w-[calc(100%+2rem)] bg-red-50 border-t border-gray-100 px-4 py-4 flex items-center gap-2">
                                <span className="text-red-600 text-sm">Answer not submitted</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Dismiss status
                                    }}
                                    className="text-red-600 text-2xl leading-none pb-1"
                                >
                                    ×
                                </button>
                            </div>
                            <hr className="-mx-4 w-[calc(100%+2rem)]" />
                            {/- Next question button -/}
                            <button
                                onClick={handleNextQuestion}
                                className="w-full flex items-center  gap-2 text-blue-600 hover:text-blue-700 font-medium py-2"
                            >
                                <span>Next question</span>
                                <IoArrowForward className="text-blue-600" size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleSwipe = () => {
        setCurrentIndex((prev) => {
            if (prev < cards.length - 1) {
                setFlippedCards({});
                return prev + 1;
            }
            return prev;
        });
    };

    const topCard = cards[currentIndex];
    const nextCard =
        currentIndex < cards.length - 1 ? cards[currentIndex + 1] : null;

    return (
        <>
            <Dialog open={open} as="div" className="relative z-50" onClose={onClose}>
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                    <DialogPanel className="w-full max-w-md bg-white rounded-3xl relative max-h-[95vh]">

                        <IoMdClose
                            className="absolute top-4 right-4 text-gray-600 cursor-pointer"
                            onClick={onClose}
                            size={22}
                        />

                        <div className="p-6 flex flex-col gap-0.5 max-h-[90vh] overflow-visible">

                            <div>
                                <h3 className="text-2xl font-semibold">{deckName || "Deck"}</h3>
                                <p className="text-sm text-gray-500">
                                    Total cards: {cards.length}
                                </p>
                            </div>

                            <div className="relative w-full min-h-[600px] flex justify-center items-center overflow-visible">

                                {isLoading ? (
                                    <div className="flex justify-center items-center py-10">
                                        <div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" />
                                    </div>
                                ) : cards.length === 0 ? (
                                    <p className="text-gray-500 text-center">No cards found.</p>
                                ) : (
                                    <div className="relative w-[90%] max-w-md flex justify-center items-center">

                                        {nextCard && (
                                            <div
                                                className="absolute w-full"
                                                style={{
                                                    bottom: "-14px",
                                                    transform: "scale(0.98)",
                                                    opacity: 0.55,
                                                    transition: "all 0.3s ease",
                                                    pointerEvents: "none",
                                                }}
                                            >
                                                {renderCard(nextCard, currentIndex + 1)}
                                            </div>
                                        )}

                                        <SwipeCard
                                            key={currentIndex}
                                            onSwipe={handleSwipe}
                                            canSwipe={currentIndex < cards.length - 1}
                                        >
                                            {renderCard(topCard, currentIndex)}
                                        </SwipeCard>
                                    </div>
                                )}
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>

            {/- Answer Modal for Memory Type Cards -/}
            <Dialog
                open={showAnswerModal}
                as="div"
                className="relative z-[60]"
                onClose={() => setShowAnswerModal(false)}
            >
                <div className="fixed inset-0 bg-slate-700/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <DialogPanel
                        className="w-full max-w-xs rounded-3xl relative overflow-visible shadow-xl answer-modal-background"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/- TOP ORANGE BADGE -/}
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex flex-col items-center z-20">
                            <div className="bg-orange-500 w-20 h-20 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                                <FaQuestionCircle className="text-white text-4xl" />
                            </div>
                        </div>

                        {/- Close button -/}
                        <IoMdClose
                            className="absolute top-4 right-4 text-black cursor-pointer z-10"
                            onClick={() => setShowAnswerModal(false)}
                            size={24}
                        />

                        {/- Content -/}
                        <div className="pt-14 pb-6 px-6 text-center">
                            <div className="bg-white rounded-2xl p-5 ">
                                {/- Modal Box Background with white background -/}
                                    {/- Answer / Hint section -/}
                                    <div
                                        className={`max-h-[300px] text-gray-900 leading-relaxed text-base ${
                                            selectedCardAnswer?.hasImageContent
                                                ? "modal-scroll-hidden"
                                                : "overflow-y-auto"
                                        }`}
                                    >
                                        {selectedCardAnswer?.hints?.length > 0 ? (
                                            selectedCardAnswer.hints.flatMap((hint, i) =>
                                                hint.parts?.map((part, j) =>
                                                    renderContentPart(part, `modal-hint-${i}-${j}`, "center")
                                                )
                                            )
                                        ) : selectedCardAnswer?.answerParts?.length > 0 ? (
                                            selectedCardAnswer.answerParts.map((part, idx) =>
                                                renderContentPart(part, `modal-answer-${idx}`, "center")
                                            )
                                        ) : (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        selectedCardAnswer?.answer ||
                                                        t("No answer available") ||
                                                        "No answer available",
                                                }}
                                            />
                                        )}
                                    </div>

                                {/- Bottom button -/}
                                <button
                                    onClick={() => setShowAnswerModal(false)}
                                    className=" bg-white mt-4 w-full hover:bg-gray-50 text-black font-semibold py-3 px-10 rounded-xl border border-gray-200 transition"
                                >
                                    {t("Got it!") || "Got it!"}
                                </button>
                            </div>

                        </div>
                    </DialogPanel>
                </div>
            </Dialog>
            
        </>
    );
}

   ============================================
   COMMENTED OUT ORIGINAL CODE - END
   ============================================ */
